// My Permissions — Wave 7 Day 1 (Priority 8).
//
// "Şu an hangi yetkilerle login'im?" UX. Shared across admin/seller/supply
// shells; role + supplyPersona drives current user resolution.
//
// V1 read-only display; V1.5+ Volkan UI to grant/revoke per-user capabilities.

import { useQuery } from '@tanstack/react-query'
import {
  CheckCircle2,
  Circle,
  ShieldCheck,
  User,
} from 'lucide-react'
import { API_BASE, USE_REAL_API } from '../../lib/api'
import { fetchMe } from '../../lib/auth'
import { formatDateTime } from '../../lib/datetime'
import { Card } from '../../components/ui/card'
import { useSupplyPersonaStore } from '../../store/role'
import {
  ALL_CAPABILITIES,
  type Capability,
  type CapabilityCategory,
  type RoleTier,
  type UserPermission,
} from '../../lib/user-permissions'
import { useT } from '../../i18n'
import { myPermissionsI18n } from '../../i18n/shared/my-permissions'

// Real membership tiers (so_owner / a1_platform_owner / warehouse_operator …)
// map onto the display RoleTier vocabulary for the label + description.
function mapRealTier(tier: string): RoleTier {
  if (tier.endsWith('owner')) return 'owner'
  if (tier === 'warehouse_operator') return 'warehouse_operator'
  // a2_* covers internal_staff + the migration-255 preset roles (ops /
  // finance / readonly) — all admin-shell staff for display purposes.
  if (tier.includes('admin') || tier.startsWith('a2_')) return 'admin'
  return 'member'
}

const CATEGORY_COLOR: Record<CapabilityCategory, string> = {
  catalog: 'bg-indigo-50 border-indigo-200 text-indigo-900',
  orders: 'bg-blue-50 border-blue-200 text-blue-900',
  pricing: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  drive: 'bg-violet-50 border-violet-200 text-violet-900',
  production: 'bg-amber-50 border-amber-200 text-amber-900',
  team: 'bg-rose-50 border-rose-200 text-rose-900',
  settings: 'bg-[var(--color-surface-muted)] border-[var(--color-border)] text-[var(--color-text)]',
  finance: 'bg-teal-50 border-teal-200 text-teal-900',
  platform: 'bg-purple-50 border-purple-200 text-purple-900',
  shipping: 'bg-cyan-50 border-cyan-200 text-cyan-900',
}

type PermissionsResponse = {
  user: UserPermission
  catalog: Capability[]
}

export function MyPermissionsPage({
  role,
}: {
  role: 'admin' | 'seller' | 'supply'
}) {
  const t = useT(myPermissionsI18n)
  const supplyPersona = useSupplyPersonaStore((s) => s.supplyPersona)
  const query = `role=${role}${role === 'supply' ? `&persona=${supplyPersona}` : ''}`

  const { data, isLoading, error } = useQuery({
    queryKey: ['me-permissions', role, supplyPersona, USE_REAL_API],
    queryFn: async (): Promise<PermissionsResponse> => {
      // Real engine: build from the logged-in JWT (granted capabilities) +
      // the static capability catalog (reference data, not secret). Maket mode:
      // the mock /me/permissions endpoint.
      if (USE_REAL_API) {
        const me = await fetchMe()
        if (!me) throw new Error('not authenticated')
        const m = me.active_membership
        const user: UserPermission = {
          user_id: me.user.id,
          user_label: m?.persona_label ?? me.user.display_name,
          org_id: m?.org_id ?? '',
          org_name: m?.persona_label ?? me.user.display_name,
          org_type: (m?.org_type ?? 'SO2') as UserPermission['org_type'],
          tier: mapRealTier(m?.tier ?? 'member'),
          effective_capabilities: me.capabilities,
          granted_at: '',
        }
        return { user, catalog: ALL_CAPABILITIES }
      }
      const res = await fetch(`${API_BASE}/me/permissions?${query}`)
      if (!res.ok) throw new Error(`fetch permissions ${res.status}`)
      return (await res.json()) as PermissionsResponse
    },
  })

  if (isLoading) {
    return (
      <Card className="p-8 text-center text-[var(--color-text-muted)] text-sm">{t.loading}</Card>
    )
  }
  if (error || !data) {
    return (
      <Card className="p-8 text-center text-rose-700 text-sm">
        {t.loadError}
      </Card>
    )
  }

  const { user, catalog } = data
  const grantedSet = new Set(user.effective_capabilities)

  // Group catalog by category
  const byCategory = catalog.reduce(
    (acc, c) => {
      if (!acc[c.category]) acc[c.category] = []
      acc[c.category].push(c)
      return acc
    },
    {} as Record<CapabilityCategory, Capability[]>,
  )

  const totalCatalog = catalog.length
  const totalGranted = catalog.filter((c) => grantedSet.has(c.id)).length
  const pct = totalCatalog
    ? Math.min(100, Math.round((totalGranted / totalCatalog) * 100))
    : 0

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-[var(--color-text)] flex items-center gap-2">
          <ShieldCheck size={22} className="text-indigo-600" aria-hidden="true" />
          {t.title}
        </h1>
        <p className="text-xs text-[var(--color-text-muted)] mt-1">
          {t.subtitle}
        </p>
      </div>

      {/* User identity card */}
      <Card className="p-4 bg-[var(--color-surface-muted)]">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center flex-shrink-0">
            <User size={20} aria-hidden="true" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h2 className="text-base font-semibold text-[var(--color-text)]">
                {user.user_label}
              </h2>
              <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-indigo-100 text-indigo-800 uppercase tracking-wide">
                {t.tiers[user.tier]}
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-[var(--color-surface-muted)] text-[var(--color-text)]">
                {user.org_type}
              </span>
            </div>
            <p className="text-xs text-[var(--color-text-muted)]">
              <strong>{user.org_name}</strong>
              <span className="text-[var(--color-text-muted)] ml-2 font-mono">{user.org_id}</span>
            </p>
            <p className="text-[11px] text-[var(--color-text-muted)] mt-1.5 italic">
              {t.tierDescriptions[user.tier]}
            </p>
          </div>
          <div className="flex-shrink-0 text-right">
            <div className="text-2xl font-semibold text-[var(--color-text)]">
              {totalGranted}
              <span className="text-sm text-[var(--color-text-muted)]">/{totalCatalog}</span>
            </div>
            <div className="text-[11px] text-[var(--color-text-muted)]">{t.granted}</div>
            <div className="mt-1 w-20 h-1 bg-[var(--color-surface-muted)] rounded-full overflow-hidden">
              <div
                className="h-full bg-indigo-600 transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Capability matrix by category */}
      <div className="space-y-3">
        {(Object.keys(byCategory) as CapabilityCategory[]).map((cat) => {
          const items = byCategory[cat]
          const grantedInCat = items.filter((c) => grantedSet.has(c.id)).length
          return (
            <Card
              key={cat}
              className={`pex-permission-category p-4 border ${CATEGORY_COLOR[cat]}`}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold inline-flex items-center gap-2">
                  {t.categories[cat]}
                </h3>
                <span className="text-[11px] font-mono">
                  {grantedInCat}/{items.length}
                </span>
              </div>
              <ul className="space-y-1.5">
                {items.map((c) => {
                  const granted = grantedSet.has(c.id)
                  const text = t.capability(c)
                  return (
                    <li
                      key={c.id}
                      className={`flex items-start gap-2 text-xs ${
                        granted ? 'text-[var(--color-text)]' : 'text-[var(--color-text-muted)]'
                      }`}
                    >
                      {granted ? (
                        <CheckCircle2
                          size={14}
                          className="flex-shrink-0 mt-0.5 text-emerald-600"
                          aria-hidden="true"
                        />
                      ) : (
                        <Circle
                          size={14}
                          className="flex-shrink-0 mt-0.5 text-[var(--color-text-muted)]"
                          aria-hidden="true"
                        />
                      )}
                      <div className="flex-1">
                        <div className="font-medium">{text.label}</div>
                        <div className={`text-[11px] ${granted ? 'text-[var(--color-text-muted)]' : 'text-[var(--color-text-muted)]'}`}>
                          {text.description}
                        </div>
                      </div>
                      <span className="text-[10px] font-mono text-[var(--color-text-muted)] flex-shrink-0">
                        {c.id}
                      </span>
                    </li>
                  )
                })}
              </ul>
            </Card>
          )
        })}
      </div>

      {user.granted_at && (
        <p className="text-[11px] text-[var(--color-text-muted)] italic text-center">
          {t.grantedAt}{formatDateTime(user.granted_at)}
        </p>
      )}
    </div>
  )
}
